import React, { useState, useRef } from 'react'

import * as styles from './Navbar.module.scss'

import { Link } from '../../molecules/link'
import { Button } from '../../molecules/button'
import ListingCard from 'src/modules/molecules/listing-card'
import { navigate } from 'gatsby'
import FlatfileLogo from 'src/modules/molecules/flatfile-logo'

const featuredPost = [
  {
    id: '1',
    category: 'Guide',
    featuredImage: '/assets/mockups/customer-guide.png',
    postTitle:
      'The Customer Success Leader’s Guide to Successful Data Onboarding',
    url: '/resources/guides/the-customer-success-leaders-guide-to-successful-data-onboarding/'
  }
]

const NavSectionTitle = ({ title }: { title: string }) => {
  return <h4 className={styles.navSectionTitle}>{title}</h4>
}

const DropdownNavLink = ({
  text,
  href,
  description
}: {
  text: string
  href: string
  description: string
}) => {
  return (
    <li className={styles.dropdownNavLink}>
      <a href={href}>
        {text}
        <p>{description}</p>
      </a>
    </li>
  )
}

const productLinks = [
  {
    title: 'Product',
    sectionName: 'product',
    links: [
      {
        text: 'Workbooks',
        href: '/product/workbooks/',
        description:
          'Our spreadsheet-like interface that lets your whole team prepare data'
      },
      {
        text: 'Mapping',
        href: '/product/mapping/',
        description:
          'AI-powered mapping that auto-matches fields to your destination schema'
      },
      {
        text: 'Configuration',
        href: '/product/configuration/',
        description:
          'Configure your Flatfile workspace with or without a technical team'
      },
      {
        text: 'Collaboration',
        href: '/product/collaboration/',
        description:
          'Collaborate with your team and customers to prepare data within a project'
      },
      {
        text: 'Security',
        href: '/security/',
        description:
          'Learn about our data security and our compliance standards'
      },
      {
        text: 'Transform',
        href: '/product/transform/',
        description:
          'Transform meets you where the mess is—and takes care of it for you.'
      }
    ]
  }
]

const DropdownProduct = () => {
  return (
    <div className={styles.dropdownMenu}>
      <div className={styles.dropdownMenuContent} id='product-dropdown'>
        <div className={styles.dropdownContentProduct}>
          <div className={styles.dropdownContentLeft}>
            {productLinks.map(link => (
              <>
                <NavSectionTitle title={link.title} />
                <ul>
                  {link.links.map(item => (
                    <li key={item.text}>
                      <DropdownNavLink
                        text={item.text}
                        href={item.href}
                        description={item.description}
                      />
                    </li>
                  ))}
                </ul>
              </>
            ))}
          </div>

          <div className={styles.dropdownContentRight}>
            <NavSectionTitle title='Related content' />
            <ListingCard
              imageSrc={featuredPost[0].featuredImage}
              title={featuredPost[0].postTitle}
              titleSize='sm'
              url={featuredPost[0].url}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const solutionsLinks = [
  {
    title: 'Solutions',
    sectionName: 'solutions',
    links: [
      {
        text: 'For service teams',
        href: '/solutions/services/',
        subCategory: 'Use Cases',
        description:
          'Use Flatfile to move customer data into your systems 70% faster'
      },
      {
        text: 'For system integrators',
        href: '/solutions/system-integrators/',
        subCategory: 'Use Cases',
        description: 'Use Flatfile to accelerate data migration projects'
      },
      {
        text: 'For NetSuite',
        href: '/editions/netsuite/',
        subCategory: 'Editions',
        description:
          'Flatfile for NetSuite Alliance Partners and Solution Providers'
      },
      {
        text: 'For Workday',
        href: '/editions/workday/',
        subCategory: 'Editions',
        description:
          'Flatfile for Workday Service Partners and Solution Providers'
      }
    ]
  }
]

const DropdownSolutions = () => {
  return (
    <div className={styles.dropdownMenu}>
      <div className={styles.dropdownMenuContent} id='solutions-dropdown'>
        <div className={styles.dropdownContentSolutions}>
          <div className={styles.dropdownContentLeft}>
            <div>
              {/* use cases here */}
              <NavSectionTitle title='Use Cases' />
              <ul>
                {solutionsLinks.map(link =>
                  link.links
                    .filter(item => item.subCategory === 'Use Cases')
                    .map(item => (
                      <li key={item.text}>
                        <DropdownNavLink
                          text={item.text}
                          href={item.href}
                          description={item.description}
                        />
                      </li>
                    ))
                )}
              </ul>
            </div>

            <div>
              {/* editions here */}
              <NavSectionTitle title='Editions' />
              {/* only items with subcategory Editions */}

              <ul>
                {solutionsLinks.map(link =>
                  link.links
                    .filter(item => item.subCategory === 'Editions')
                    .map(item => (
                      <li key={item.text}>
                        <DropdownNavLink
                          text={item.text}
                          href={item.href}
                          description={item.description}
                        />
                      </li>
                    ))
                )}
              </ul>
            </div>
          </div>

          <div className={styles.dropdownContentRight}></div>
        </div>
      </div>
    </div>
  )
}

const companyLinks = [
  {
    title: 'Company',
    sectionName: 'company',
    links: [
      {
        text: 'About',
        href: '/about/',
        description:
          'Learn about our team and our vision for the future of data prep'
      },
      {
        text: 'News',
        href: '/news/',
        description: 'Read the latest Flatfile company news and press coverage'
      },
      {
        text: 'Careers',
        href: '/careers/',
        badgeText: "We're hiring!",
        description:
          "See what it's like to work at Flatfile and explore open roles"
      },
      {
        text: 'Contact us',
        href: '/contact-us/',
        description:
          'Connect with us with questions about our product or for support'
      }
    ]
  }
]

const companySocialLinks = [
  {
    text: 'Facebook',
    href: 'https://facebook.com/flatfilers/'
  },
  {
    text: 'X',
    href: 'https://twitter.com/flatfile/'
  },
  {
    text: 'LinkedIn',
    href: 'https://linkedin.com/company/flatfile'
  },
  {
    text: 'Github',
    href: 'https://github.com/FlatFilers'
  },
  {
    text: 'Developer Slack',
    href: 'https://flatfile.com/join-slack/'
  }
]

const DropdownCompany = () => {
  return (
    <div className={styles.dropdownMenu}>
      <div className={styles.dropdownMenuContent} id='company-dropdown'>
        <div className={styles.dropdownContentCompany}>
          <div className={styles.dropdownContentLeft}>
            {companyLinks.map(link => (
              <>
                <NavSectionTitle title={link.title} />
                <ul>
                  {link.links.map(item => (
                    <li key={item.text}>
                      <DropdownNavLink
                        text={item.text}
                        href={item.href}
                        description={item.description}
                      />
                    </li>
                  ))}
                </ul>
              </>
            ))}
          </div>
          <div className={styles.dropdownContentRight}>
            {/* social links */}
            <NavSectionTitle title='Socials' />
            <ul className={styles.navSocialLinks}>
              {companySocialLinks.map(link => (
                <li key={link.text}>
                  <a href={link.href}>{link.text}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

const resourcesLinks = [
  {
    title: 'Resources',
    sectionName: 'resources',
    links: [
      {
        text: 'Blog',
        href: '/blog/',
        description:
          'Find product features, guest posts, and industry insights on our blog'
      },
      {
        text: 'Customers',
        href: '/resources/case-studies/',
        description:
          "Explore Flatfile's data prep case studies for real-world use cases and wins"
      },
      {
        text: "What's new in product",
        href: '/whats-new/',
        description: 'Stay up to date with the latest Flatfile product releases'
      },
      {
        text: 'Guides & reports',
        href: '/resources/guides/',
        description:
          "Download Flatfile's guides, reports, and checklists for insights and tactics"
      },
      {
        text: 'Events',
        href: '/resources/webinars/',
        description: "See Flatfile's latest and upcoming events and webinars"
      },
      {
        text: 'Why Flatfile?',
        href: '/why-flatfile/',
        description:
          'Learn the most common reasons enterprises seek out Flatfile'
      },
      {
        text: 'What is Zero Trust AI?',
        href: '/resources/zero-trust-ai/',
        description:
          'Learn about our framework for enterprise adoption of imperfect AI systems'
      },
      {
        text: 'Knowledge base',
        href: 'https://support.flatfile.com/',
        description: 'Find tutorials and helpful guides for Flatfile features'
      },
      {
        text: 'Developer docs',
        href: '/docs/',
        description:
          'Set up, configure, and integrate Flatfile with developer-ready docs'
      }
    ]
  }
]

const DropdownResources = () => {
  return (
    <div className={styles.dropdownMenu}>
      <div className={styles.dropdownMenuContent} id='resources-dropdown'>
        <div className={styles.dropdownContentResources}>
          <div className={styles.dropdownContentLeft}>
            {resourcesLinks.map(link => (
              <>
                <NavSectionTitle title={link.title} />
                <ul>
                  {link.links.map(item => (
                    <li key={item.text}>
                      <DropdownNavLink
                        text={item.text}
                        href={item.href}
                        description={item.description}
                      />
                    </li>
                  ))}
                </ul>
              </>
            ))}
          </div>
          <div className={styles.dropdownContentRight}></div>
        </div>
      </div>
    </div>
  )
}

// Login dropdown component
const DropdownLogin = () => {
  return (
    <div className={styles.loginDropdownMenu}>
      <div className={styles.loginDropdownContent}>
        <Button
          href='https://platform.flatfile.com/oauth/dashboard'
          variant='primary'
          iconSpacing='medium'
          showLeadIcon
          leadIconSrc='/img/icons/login.svg'
          size='small'
        >
          Platform Login
        </Button>
        <p className={styles.unsupportedVersions}>
          Unsupported Versions: Flatfile v0.3
        </p>
      </div>
    </div>
  )
}

const Navbar: React.FC = ({
  announcementBarOpen
}: {
  announcementBarOpen: boolean
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

  const toggleAccordion = (sectionName: string) => {
    console.log(
      'Toggle accordion:',
      sectionName,
      'Current active:',
      activeAccordion
    )
    setActiveAccordion(activeAccordion === sectionName ? null : sectionName)
  }

  // Handle dropdown hover
  const handleDropdownHover = (dropdownName: string | null) => {
    setActiveDropdown(dropdownName)
  }

  // Handle focus events
  const handleFocus = (dropdownName: string) => {
    setActiveDropdown(dropdownName)
  }

  // Handle blur events
  const handleBlur = (e: React.FocusEvent) => {
    // Only close the dropdown if focus is moving outside the dropdown container
    // This prevents the dropdown from closing when clicking on items inside it
    const currentTarget = e.currentTarget
    const relatedTarget = e.relatedTarget as Node

    // If relatedTarget is null or not a child of currentTarget, close the dropdown
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setActiveDropdown(null)
    }
  }

  // Get class name for dropdown items
  const getDropdownClassName = (dropdownName: string) => {
    return `${styles.navbarItem} ${styles.dropdown} ${
      activeDropdown === dropdownName ? styles.active : ''
    }`
  }

  return (
    <>
      {/* Overlay that appears when dropdown is active */}
      <div
        className={`${styles.navOverlay} ${
          activeDropdown ? styles.active : ''
        }`}
        onClick={() => handleDropdownHover(null)}
      />

      {/* if announcementBarOpen is true, add a class to the navbar */}
      <nav
        className={`${styles.navbar} ${
          announcementBarOpen ? styles.announcementBarOpen : ''
        }`}
      >
        <div className={styles.navbarInnerContainer}>
          {/* <div className={styles.navbarShadow}></div> */}
          <div className={styles.navbarContent}>
            {/* Logo */}

            <a href='/' className={styles.navbarLogoLink}>
              <FlatfileLogo />
            </a>

            {/* Desktop Navigation */}
            <div className={styles.navbarMenu}>
              <div
                className={getDropdownClassName('product')}
                onMouseEnter={() => handleDropdownHover('product')}
                onMouseLeave={() => handleDropdownHover(null)}
                onFocus={() => handleFocus('product')}
                onBlur={handleBlur}
              >
                <button
                  type='button'
                  className={styles.navbarDropdownTitle}
                  aria-expanded={activeDropdown === 'product'}
                  aria-controls='product-dropdown'
                >
                  <span>Product</span>
                </button>

                <DropdownProduct />
              </div>
              <div
                className={getDropdownClassName('solutions')}
                onMouseEnter={() => handleDropdownHover('solutions')}
                onMouseLeave={() => handleDropdownHover(null)}
                onFocus={() => handleFocus('solutions')}
                onBlur={handleBlur}
              >
                <button
                  type='button'
                  className={styles.navbarDropdownTitle}
                  aria-expanded={activeDropdown === 'solutions'}
                  aria-controls='solutions-dropdown'
                >
                  <span>Solutions</span>
                </button>

                <DropdownSolutions />
              </div>
              <div
                className={getDropdownClassName('resources')}
                onMouseEnter={() => handleDropdownHover('resources')}
                onMouseLeave={() => handleDropdownHover(null)}
                onFocus={() => handleFocus('resources')}
                onBlur={handleBlur}
              >
                <button
                  type='button'
                  className={styles.navbarDropdownTitle}
                  aria-expanded={activeDropdown === 'resources'}
                  aria-controls='resources-dropdown'
                  onClick={() => {
                    navigate('/resources/')
                  }}
                >
                  <span>Resources</span>
                </button>

                <DropdownResources />
              </div>
              <div
                className={getDropdownClassName('company')}
                onMouseEnter={() => handleDropdownHover('company')}
                onMouseLeave={() => handleDropdownHover(null)}
                onFocus={() => handleFocus('company')}
                onBlur={handleBlur}
              >
                <button
                  type='button'
                  className={styles.navbarDropdownTitle}
                  aria-expanded={activeDropdown === 'company'}
                  aria-controls='company-dropdown'
                >
                  <span>Company</span>
                </button>

                <DropdownCompany />
              </div>

              <div className={styles.navbarItem}>
                <Link variant='dark' href='/pricing/'>
                  Pricing
                </Link>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className={styles.navbarActions}>
            <div
              className={`${styles.loginLink} ${styles.dropdown} ${
                activeDropdown === 'login' ? styles.active : ''
              }`}
              onMouseEnter={() => handleDropdownHover('login')}
              onMouseLeave={() => handleDropdownHover(null)}
              onFocus={() => handleFocus('login')}
              onBlur={handleBlur}
            >
              <Link
                variant='dark'
                href='https://platform.flatfile.com/oauth/dashboard'
              >
                Log in
              </Link>
              <DropdownLogin />
            </div>

            <Button href='/demo/' variant='secondary' size='small'>
              Book a demo
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className={`${styles.navbarToggle} ${isOpen ? styles.open : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className={styles.navbarToggleBar}></div>
            <div className={styles.navbarToggleBar}></div>
          </button>

          {/* Mobile Menu - Always in DOM but visibility controlled by classes */}
          <div
            className={`${styles.mobileMenu} ${
              isOpen ? styles.fadeIn : styles.fadeOut
            }`}
          >
            <div className={styles.mobileMenuInnerContainer}>
              <div className={styles.mobileMenuItems}>
                {/* Product Accordion */}
                <div className={styles.mobileAccordion}>
                  <button
                    className={`${styles.mobileAccordionToggle} ${
                      activeAccordion === 'product' ? styles.active : ''
                    }`}
                    onClick={() => toggleAccordion('product')}
                    aria-expanded={activeAccordion === 'product' ? true : false}
                  >
                    <span>Product</span>
                    <svg
                      width='12'
                      height='8'
                      viewBox='0 0 12 8'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M1 1.5L6 6.5L11 1.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  <div
                    className={`${styles.mobileAccordionContent} ${
                      activeAccordion === 'product' ? styles.open : ''
                    }`}
                  >
                    <ul>
                      {productLinks[0].links.map(item => (
                        <li key={item.text}>
                          <a href={item.href}>{item.text}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Solutions Accordion */}
                <div className={styles.mobileAccordion}>
                  <button
                    className={`${styles.mobileAccordionToggle} ${
                      activeAccordion === 'solutions' ? styles.active : ''
                    }`}
                    onClick={() => toggleAccordion('solutions')}
                    aria-expanded={
                      activeAccordion === 'solutions' ? true : false
                    }
                  >
                    <span>Solutions</span>
                    <svg
                      width='12'
                      height='8'
                      viewBox='0 0 12 8'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M1 1.5L6 6.5L11 1.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  <div
                    className={`${styles.mobileAccordionContent} ${
                      activeAccordion === 'solutions' ? styles.open : ''
                    }`}
                  >
                    <ul>
                      {solutionsLinks[0].links.map(item => (
                        <li key={item.text}>
                          <a href={item.href}>{item.text}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Resources Accordion */}
                <div className={styles.mobileAccordion}>
                  <button
                    className={`${styles.mobileAccordionToggle} ${
                      activeAccordion === 'resources' ? styles.active : ''
                    }`}
                    onClick={() => toggleAccordion('resources')}
                    aria-expanded={
                      activeAccordion === 'resources' ? true : false
                    }
                  >
                    <span>Resources</span>
                    <svg
                      width='12'
                      height='8'
                      viewBox='0 0 12 8'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M1 1.5L6 6.5L11 1.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  <div
                    className={`${styles.mobileAccordionContent} ${
                      activeAccordion === 'resources' ? styles.open : ''
                    }`}
                  >
                    <ul>
                      {resourcesLinks[0].links.map(item => (
                        <li key={item.text}>
                          <a href={item.href}>{item.text}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Company Accordion */}
                <div className={styles.mobileAccordion}>
                  <button
                    className={`${styles.mobileAccordionToggle} ${
                      activeAccordion === 'company' ? styles.active : ''
                    }`}
                    onClick={() => toggleAccordion('company')}
                    aria-expanded={activeAccordion === 'company' ? true : false}
                  >
                    <span>Company</span>
                    <svg
                      width='12'
                      height='8'
                      viewBox='0 0 12 8'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M1 1.5L6 6.5L11 1.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  <div
                    className={`${styles.mobileAccordionContent} ${
                      activeAccordion === 'company' ? styles.open : ''
                    }`}
                  >
                    <ul>
                      {companyLinks[0].links.map(item => (
                        <li key={item.text}>
                          <a href={item.href}>{item.text}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Pricing Link */}
                <div className={styles.mobileLink}>
                  <a href='/pricing/'>Pricing</a>
                </div>

                {/* Login Link */}
                <div className={styles.mobileLink}>
                  <a href='https://platform.flatfile.com/oauth/dashboard'>
                    Log in
                  </a>
                </div>
              </div>
              <Button href='/demo/' variant='secondary'>
                Book a demo
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

export default Navbar
